import {
  createContext,
  useContext,
  useMemo,
  type CSSProperties,
  type FC,
  type ReactNode,
} from 'react';
import classNames from 'classnames';
import styles from './card-slot.module.scss';

interface CardPinState {
  /** True while this card is docked to the top of the column. */
  pinned: boolean;
  /** Docks or undocks it. */
  toggle: () => void;
}

const CardPinContext = createContext<CardPinState | null>(null);

/**
 * The pin state of the card currently rendering, if it is pinnable.
 * @return {CardPinState | null} The state, or null outside a CardSlot.
 */
export const useCardPin = (): CardPinState | null => useContext(CardPinContext);

interface Props {
  /** The section id this card is pinned and toggled by. */
  sectionId: string;
  pinnedIds: Set<string>;
  onTogglePin: (sectionId: string) => void;
  /**
   * Distance from the top of the column this card docks at — the summed height of the
   * cards docked above it, so a stack of pinned cards tiles instead of overlapping.
   * Ignored unless this card is pinned.
   */
  stickyTop: number;
  /**
   * Hides the card without unmounting it. Unmounting is destructive here: the Map's
   * `MeasureControl` throws from its cleanup against a torn-down map, and the Explorer
   * would rebuild its WebGL and SciChart stack. Hiding also preserves card state.
   */
  hidden?: boolean;
  children: ReactNode;
}

/**
 * Wraps one detail card so it can be docked without every card carrying pin props.
 *
 * Docking happens in place, with flex `order` and `position: sticky`, rather than by
 * re-parenting into a pinned container: re-parenting remounts, and remounting these
 * cards is destructive (see the `hidden` prop above).
 * @param {Props} props The section id, the pinned set, the toggle, the dock offset, and
 * the card.
 * @return {JSX.Element} The wrapped card.
 */
export const CardSlot: FC<Props> = ({
  sectionId,
  pinnedIds,
  onTogglePin,
  stickyTop,
  hidden,
  children,
}) => {
  // A hidden card is out of the page, so it cannot also be docked to the top of it.
  const pinned = pinnedIds.has(sectionId) && !hidden;
  const value = useMemo<CardPinState>(
    () => ({ pinned, toggle: () => onTogglePin(sectionId) }),
    [pinned, sectionId, onTogglePin]
  );

  // Only the offset is inline: it is measured at runtime, so it cannot live in the
  // stylesheet. Everything else about docking is in the module.
  const style: CSSProperties | undefined = pinned
    ? { top: `${stickyTop}px` }
    : undefined;

  return (
    <div
      data-section={sectionId}
      data-pinned={pinned || undefined}
      className={classNames(pinned ? styles.docked : styles.slot, {
        [styles.hidden]: hidden,
      })}
      style={style}
    >
      <CardPinContext.Provider value={value}>
        {children}
      </CardPinContext.Provider>
    </div>
  );
};
